"use client";

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import styles from "./LandingHeader.module.css";

const NAV_ITEMS = [
  { label: "Video hosting", href: "/" },
  { label: "Recording", href: "/products/record-and-edit" },
];

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export default function LandingHeader({ isAuthenticated = false }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const pathname = usePathname();

  const navListRef = useRef(null);
  const itemRefs = useRef({});
  const indicatorRef = useRef(null);
  const wasVisibleRef = useRef(false);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, visible: false });

  const isActive = (href) => pathname === href;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const measureAndSet = useCallback(() => {
    const activeItem = NAV_ITEMS.find((item) => item.href === pathname);
    if (!activeItem) {
      wasVisibleRef.current = false;
      setIndicator((prev) => ({ ...prev, visible: false }));
      return;
    }
    const itemEl = itemRefs.current[activeItem.href];
    const containerEl = navListRef.current;
    if (!itemEl || !containerEl) return;
    const itemRect = itemEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    const nextLeft = itemRect.left - containerRect.left;
    const nextWidth = itemRect.width;

    // If the indicator was hidden (or never measured), snap to the new position
    // without animating, then let opacity fade it in.
    if (!wasVisibleRef.current && indicatorRef.current) {
      const el = indicatorRef.current;
      const prevTransition = el.style.transition;
      el.style.transition = "none";
      el.style.transform = `translateX(${nextLeft}px)`;
      el.style.width = `${nextWidth}px`;
      // Force a reflow so the transition reset takes effect before re-enabling.
      void el.offsetHeight;
      el.style.transition = prevTransition;
    }
    wasVisibleRef.current = true;
    setIndicator({ left: nextLeft, width: nextWidth, visible: true });
  }, [pathname]);

  useIsomorphicLayoutEffect(() => {
    measureAndSet();
  }, [measureAndSet]);

  useEffect(() => {
    const handleResize = () => measureAndSet();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [measureAndSet]);

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        <Link href="/" className={styles.logoContainer}>
          <Image
            src="https://assets.flare.link/logoFlare.png"
            alt="Flare"
            width={90}
            height={20}
            priority
            quality={100}
          />
        </Link>

        <nav className={styles.nav}>
          <div className={styles.navList} ref={navListRef}>
            <span
              ref={indicatorRef}
              className={styles.activeIndicator}
              style={{
                transform: `translateX(${indicator.left}px)`,
                width: `${indicator.width}px`,
                opacity: indicator.visible ? 1 : 0,
              }}
              aria-hidden="true"
            />

            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                ref={(el) => {
                  if (el) itemRefs.current[item.href] = el;
                  else delete itemRefs.current[item.href];
                }}
                className={`${styles.navLink} ${isActive(item.href) ? styles.active : ""}`}
              >
                {item.label}
              </Link>
            ))}

            {/* Resources Dropdown */}
            <div className={styles.dropdownContainer} ref={dropdownRef}>
              <button
                type="button"
                className={`${styles.navLink} ${styles.dropdownTrigger} ${resourcesOpen ? styles.dropdownOpen : ""}`}
                onClick={() => setResourcesOpen(!resourcesOpen)}
              >
                Resources
                <svg
                  className={styles.chevron}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {resourcesOpen && (
                <div className={styles.dropdownMenu}>
                  <div className={styles.dropdownColumn}>
                    <span className={styles.dropdownLabel}>Learn</span>
                    <Link href="/help" className={styles.dropdownItem} onClick={() => setResourcesOpen(false)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                      </svg>
                      Documentation
                    </Link>
                  </div>
                  <div className={styles.dropdownColumn}>
                    <span className={styles.dropdownLabel}>Support</span>
                    <Link href="/support" className={styles.dropdownItem} onClick={() => setResourcesOpen(false)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      Help Center
                    </Link>
                    <a href="mailto:support@flare.link" className={styles.dropdownItem} onClick={() => setResourcesOpen(false)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      Contact Us
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </nav>

        <div className={styles.authButtons}>
          {isAuthenticated ? (
            <Link href="/library" className={styles.buttonPrimary}>
              Go to Library
            </Link>
          ) : (
            <>
              <Link href="/login" className={styles.linkSignIn}>
                Sign in
              </Link>
              <Link href="/register" className={styles.buttonPrimary}>
                Sign up
              </Link>
            </>
          )}
        </div>

        <button
          className={styles.mobileMenuBtn}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className={styles.mobileMenu}>
          <nav className={styles.mobileNav}>
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`${styles.mobileNavLink} ${isActive(item.href) ? styles.mobileNavLinkActive : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className={styles.mobileNavSection}>
              <span className={styles.mobileNavLabel}>Resources</span>
              <Link href="/help" className={styles.mobileNavLink} onClick={() => setMobileMenuOpen(false)}>
                Documentation
              </Link>
              <Link href="/support" className={styles.mobileNavLink} onClick={() => setMobileMenuOpen(false)}>
                Help Center
              </Link>
            </div>
          </nav>
          <div className={styles.mobileAuthButtons}>
            {isAuthenticated ? (
              <Link href="/library" className={styles.buttonPrimary} onClick={() => setMobileMenuOpen(false)}>
                Go to Library
              </Link>
            ) : (
              <>
                <Link href="/login" className={styles.mobileSignIn} onClick={() => setMobileMenuOpen(false)}>
                  Sign in
                </Link>
                <Link href="/register" className={styles.buttonPrimary} onClick={() => setMobileMenuOpen(false)}>
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
